import React, { useCallback, useEffect, useState, useRef } from 'react';
import { LoaderIcon, AlertCircleIcon } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const RERENDER_DEBOUNCE_MS = 300;
const ZOOM_SMOOTHING = 0.3;
interface PdfPreviewViewerProps {
  pdfBlob: Blob | null;
  darkMode: boolean;
}
function getTouchDistance(t1: Touch, t2: Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}
function getTouchMidpoint(t1: Touch, t2: Touch) {
  return {
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2
  };
}
export function PdfPreviewViewer({
  pdfBlob,
  darkMode
}: PdfPreviewViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const touchAreaRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const renderedZoomRef = useRef(1);
  const reRenderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentPageRef = useRef(1);
  const gestureRef = useRef({
    isGesturing: false,
    isPinching: false,
    isPanning: false,
    initialDistance: 0,
    initialZoom: 1,
    lastPanX: 0,
    lastPanY: 0,
    startX: 0,
    startY: 0,
    currentZoom: 1,
    currentPanX: 0,
    currentPanY: 0,
    smoothedZoom: 1,
    prevFrameZoom: 1,
    lastTapTime: 0
  });
  const baseDimsRef = useRef({
    width: 0,
    height: 0
  });
  // Clamp pan so content edges don't go past viewport edges.
  // Uses baseDimsRef (stable 1x dimensions) instead of container.clientWidth/Height
  // to avoid incorrect bounds when canvas re-renders at different zoom levels.
  const clampPan = useCallback((px: number, py: number, zoom: number) => {
    if (zoom <= 1) return {
      x: 0,
      y: 0
    };
    const cw = baseDimsRef.current.width;
    const ch = baseDimsRef.current.height;
    if (!cw || !ch) return {
      x: 0,
      y: 0
    };
    const visW = cw * zoom;
    const visH = ch * zoom;
    const minPanX = -(visW - cw);
    const maxPanX = 0;
    const minPanY = -(visH - ch);
    const maxPanY = 0;
    return {
      x: Math.max(minPanX, Math.min(maxPanX, px)),
      y: Math.max(minPanY, Math.min(maxPanY, py))
    };
  }, []);
  // Apply CSS transform. cssScale is clamped to >= 1 to prevent the canvas
  // from shrinking below its natural size (which causes blank space / flicker).
  // During zoom-out, the proactive 1x re-render ensures renderedZoom drops to 1
  // quickly, making this clamp a brief safety net only.
  const applyTransform = useCallback((zoom: number, px: number, py: number) => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    const cssScale = Math.max(1, zoom / renderedZoomRef.current);
    wrapper.style.transform = `translate(${px}px, ${py}px) scale(${cssScale})`;
  }, []);
  const updateTouchAction = useCallback((zoomed: boolean) => {
    const el = touchAreaRef.current;
    if (el) el.style.touchAction = zoomed ? 'none' : 'pan-y';
  }, []);
  const scheduleReRender = useCallback((pageNum: number, zoom: number) => {
    if (reRenderTimerRef.current) clearTimeout(reRenderTimerRef.current);
    reRenderTimerRef.current = setTimeout(() => {
      reRenderTimerRef.current = null;
      renderPageAtZoom(pageNum, zoom);
    }, RERENDER_DEBOUNCE_MS);
  }, []);
  // Render a specific page at a given zoom level
  const renderPageAtZoom = useCallback(async (pageNum: number, targetZoom: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!pdfDoc || !canvas || !container) return;
    try {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          /* ignore */
        }
        renderTaskRef.current = null;
      }
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({
        scale: 1
      });
      const containerWidth = container.clientWidth;
      const baseScale = containerWidth / viewport.width;
      const renderScale = baseScale * targetZoom;
      const scaledViewport = page.getViewport({
        scale: renderScale
      });
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(scaledViewport.width * dpr);
      canvas.height = Math.floor(scaledViewport.height * dpr);
      const cssWidth = Math.floor(scaledViewport.width);
      const cssHeight = Math.floor(scaledViewport.height);
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      const baseW = Math.floor(containerWidth);
      const baseH = Math.floor(containerWidth / viewport.width * viewport.height);
      baseDimsRef.current = {
        width: baseW,
        height: baseH
      };
      // Update the fixed viewport height so the touchArea clips correctly
      setViewportHeight(baseH);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const renderTask = page.render({
        canvasContext: ctx,
        viewport: scaledViewport
      });
      renderTaskRef.current = renderTask;
      await renderTask.promise;
      renderTaskRef.current = null;
      const g = gestureRef.current;
      renderedZoomRef.current = targetZoom;
      // After re-render, update the visual transform to match current state.
      const wrapper = canvasWrapperRef.current;
      if (wrapper) {
        wrapper.style.transition = 'none';
        const newCssScale = Math.max(1, g.currentZoom / targetZoom);
        wrapper.style.transform = `translate(${g.currentPanX}px, ${g.currentPanY}px) scale(${newCssScale})`;
        wrapper.offsetHeight; // force reflow
      }
    } catch (err: any) {
      if (err?.name === 'RenderingCancelledException') return;
      console.error('[PdfPreviewViewer] Render error:', err);
      setError('Failed to render PDF page');
    }
  }, []);
  const renderPage = useCallback((pageNum: number) => renderPageAtZoom(pageNum, 1), [renderPageAtZoom]);
  // --- Native touch event handlers ---
  useEffect(() => {
    const el = touchAreaRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent) => {
      const g = gestureRef.current;
      const container = containerRef.current;
      if (e.touches.length === 2) {
        e.preventDefault();
        g.isPinching = true;
        g.isPanning = false;
        g.isGesturing = true;
        g.initialDistance = getTouchDistance(e.touches[0], e.touches[1]);
        g.initialZoom = g.currentZoom;
        g.smoothedZoom = g.currentZoom;
        g.prevFrameZoom = g.currentZoom;
        // Cancel any pending debounced re-render
        if (reRenderTimerRef.current) {
          clearTimeout(reRenderTimerRef.current);
          reRenderTimerRef.current = null;
        }
        // PROACTIVE FIX: If canvas is rendered at a zoom > 1, immediately
        // re-render at 1x so that zoom-out CSS scale stays >= 1 (no shrinking).
        // This is fast because 1x is a smaller canvas than the current high-res one.
        if (renderedZoomRef.current > 1.05) {
          renderPageAtZoom(currentPageRef.current, 1);
        }
      } else if (e.touches.length === 1 && g.currentZoom > 1) {
        e.preventDefault();
        g.isPanning = true;
        g.isPinching = false;
        g.isGesturing = true;
        g.startX = e.touches[0].clientX;
        g.startY = e.touches[0].clientY;
        g.lastPanX = g.currentPanX;
        g.lastPanY = g.currentPanY;
        if (reRenderTimerRef.current) {
          clearTimeout(reRenderTimerRef.current);
          reRenderTimerRef.current = null;
        }
      }
      // Double-tap detection
      if (e.touches.length === 1) {
        const now = Date.now();
        if (now - g.lastTapTime < 300) {
          e.preventDefault();
          if (g.currentZoom > 1.05) {
            // Reset to 1x
            g.currentZoom = 1;
            g.smoothedZoom = 1;
            g.prevFrameZoom = 1;
            g.currentPanX = 0;
            g.currentPanY = 0;
            applyTransform(1, 0, 0);
            updateTouchAction(false);
            scheduleReRender(currentPageRef.current, 1);
          } else if (container) {
            // Zoom to 2x centered on tap point
            const newZoom = 2;
            const rect = container.getBoundingClientRect();
            const tapX = e.touches[0].clientX - rect.left;
            const tapY = e.touches[0].clientY - rect.top;
            const oldZoom = g.currentZoom;
            const oldCssScale = Math.max(1, oldZoom / renderedZoomRef.current);
            const newCssScale = Math.max(1, newZoom / renderedZoomRef.current);
            const contentX = (tapX - g.currentPanX) / oldCssScale;
            const contentY = (tapY - g.currentPanY) / oldCssScale;
            let newPanX = tapX - contentX * newCssScale;
            let newPanY = tapY - contentY * newCssScale;
            g.currentZoom = newZoom;
            g.smoothedZoom = newZoom;
            g.prevFrameZoom = newZoom;
            const clamped = clampPan(newPanX, newPanY, newZoom);
            g.currentPanX = clamped.x;
            g.currentPanY = clamped.y;
            applyTransform(newZoom, clamped.x, clamped.y);
            updateTouchAction(true);
            scheduleReRender(currentPageRef.current, newZoom);
          }
          g.lastTapTime = 0;
          return;
        }
        g.lastTapTime = now;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      const container = containerRef.current;
      if (g.isPinching && e.touches.length === 2 && container) {
        e.preventDefault();
        const newDistance = getTouchDistance(e.touches[0], e.touches[1]);
        const rawScaleFactor = newDistance / g.initialDistance;
        let rawZoom = g.initialZoom * rawScaleFactor;
        rawZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, rawZoom));
        const smoothed = g.smoothedZoom + (rawZoom - g.smoothedZoom) * (1 - ZOOM_SMOOTHING);
        g.smoothedZoom = smoothed;
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, smoothed));
        // Focal point: keep the midpoint between fingers stationary
        const mid = getTouchMidpoint(e.touches[0], e.touches[1]);
        const rect = container.getBoundingClientRect();
        const focalX = mid.x - rect.left;
        const focalY = mid.y - rect.top;
        const oldZoom = g.prevFrameZoom;
        // Use clamped cssScale for focal point math to stay consistent with visual
        const oldCssScale = Math.max(1, oldZoom / renderedZoomRef.current);
        const newCssScale = Math.max(1, newZoom / renderedZoomRef.current);
        const contentX = (focalX - g.currentPanX) / oldCssScale;
        const contentY = (focalY - g.currentPanY) / oldCssScale;
        let newPanX = focalX - contentX * newCssScale;
        let newPanY = focalY - contentY * newCssScale;
        g.currentZoom = newZoom;
        g.prevFrameZoom = newZoom;
        // Let clampPan handle the 1x case (returns 0,0 for zoom <= 1)
        const clamped = clampPan(newPanX, newPanY, newZoom);
        g.currentPanX = clamped.x;
        g.currentPanY = clamped.y;
        applyTransform(newZoom, clamped.x, clamped.y);
        updateTouchAction(newZoom > 1.05);
      } else if (g.isPanning && e.touches.length === 1 && g.currentZoom > 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - g.startX;
        const dy = e.touches[0].clientY - g.startY;
        const newPanX = g.lastPanX + dx;
        const newPanY = g.lastPanY + dy;
        const clamped = clampPan(newPanX, newPanY, g.currentZoom);
        g.currentPanX = clamped.x;
        g.currentPanY = clamped.y;
        applyTransform(g.currentZoom, clamped.x, clamped.y);
      }
    };
    const onTouchEnd = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (e.touches.length === 0) {
        g.isGesturing = false;
        g.isPinching = false;
        g.isPanning = false;
        // Smooth snap to 1x if close — clampPan handles pan reset naturally
        if (g.currentZoom < 1.05) {
          g.currentZoom = 1;
          g.smoothedZoom = 1;
          g.prevFrameZoom = 1;
          const clamped = clampPan(g.currentPanX, g.currentPanY, 1);
          g.currentPanX = clamped.x;
          g.currentPanY = clamped.y;
          applyTransform(1, clamped.x, clamped.y);
          updateTouchAction(false);
        }
        const finalZoom = g.currentZoom;
        const zoomDelta = Math.abs(finalZoom - renderedZoomRef.current);
        if (zoomDelta > 0.05) {
          scheduleReRender(currentPageRef.current, finalZoom);
        }
      } else if (e.touches.length === 1 && g.isPinching) {
        // Pinch-to-pan transition
        g.isPinching = false;
        g.isPanning = true;
        g.startX = e.touches[0].clientX;
        g.startY = e.touches[0].clientY;
        g.lastPanX = g.currentPanX;
        g.lastPanY = g.currentPanY;
      }
    };
    el.addEventListener('touchstart', onTouchStart, {
      passive: false
    });
    el.addEventListener('touchmove', onTouchMove, {
      passive: false
    });
    el.addEventListener('touchend', onTouchEnd, {
      passive: false
    });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [applyTransform, clampPan, updateTouchAction, scheduleReRender, renderPageAtZoom]);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  // Load PDF
  useEffect(() => {
    if (!pdfBlob) {
      pdfDocRef.current = null;
      setNumPages(0);
      setCurrentPage(1);
      setError(null);
      return;
    }
    gestureRef.current.currentZoom = 1;
    gestureRef.current.smoothedZoom = 1;
    gestureRef.current.prevFrameZoom = 1;
    gestureRef.current.currentPanX = 0;
    gestureRef.current.currentPanY = 0;
    renderedZoomRef.current = 1;
    updateTouchAction(false);
    if (canvasWrapperRef.current) {
      canvasWrapperRef.current.style.transform = 'translate(0px, 0px) scale(1)';
    }
    let cancelled = false;
    const loadPdf = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const arrayBuffer = await pdfBlob.arrayBuffer();
        if (cancelled) return;
        const loadingTask = pdfjsLib.getDocument({
          data: arrayBuffer
        });
        const pdfDoc = await loadingTask.promise;
        if (cancelled) {
          pdfDoc.destroy();
          return;
        }
        pdfDocRef.current = pdfDoc;
        setNumPages(pdfDoc.numPages);
        setCurrentPage(1);
        await renderPage(1);
      } catch (err) {
        if (!cancelled) {
          console.error('[PdfPreviewViewer] Load error:', err);
          setError('Failed to load PDF preview');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadPdf();
    return () => {
      cancelled = true;
      if (reRenderTimerRef.current) clearTimeout(reRenderTimerRef.current);
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          /* ignore */
        }
      }
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [pdfBlob, renderPage, updateTouchAction]);
  // Re-render on container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pdfDocRef.current) return;
    const observer = new ResizeObserver(() => {
      if (pdfDocRef.current) {
        renderPageAtZoom(currentPage, gestureRef.current.currentZoom);
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [currentPage, renderPageAtZoom]);
  const goToPage = (page: number) => {
    if (page < 1 || page > numPages) return;
    gestureRef.current.currentZoom = 1;
    gestureRef.current.smoothedZoom = 1;
    gestureRef.current.prevFrameZoom = 1;
    gestureRef.current.currentPanX = 0;
    gestureRef.current.currentPanY = 0;
    renderedZoomRef.current = 1;
    updateTouchAction(false);
    if (canvasWrapperRef.current) {
      canvasWrapperRef.current.style.transform = 'translate(0px, 0px) scale(1)';
    }
    setCurrentPage(page);
    renderPage(page);
  };
  return <div ref={containerRef} className="w-full flex flex-col items-center" data-id="element-10609">
      <div ref={touchAreaRef} className="w-full relative overflow-hidden" style={{
      touchAction: 'pan-y',
      height: viewportHeight > 0 ? `${viewportHeight}px` : 'auto'
    }} data-id="element-10610">
        {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/50 z-10 min-h-[200px]" data-id="element-10611">
            <div className="flex flex-col items-center" data-id="element-10612">
              <LoaderIcon className="h-6 w-6 animate-spin text-[#3B82F6] mb-2" data-id="element-10613" />
              <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10614">
                Loading preview...
              </span>
            </div>
          </div>}

        {error && <div className="flex flex-col items-center justify-center py-8" data-id="element-10615">
            <AlertCircleIcon className={`h-8 w-8 mb-2 ${darkMode ? 'text-red-400' : 'text-red-500'}`} data-id="element-10616" />
            <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`} data-id="element-10617">
              {error}
            </p>
          </div>}

        <div ref={canvasWrapperRef} style={{
        transformOrigin: '0 0',
        willChange: 'transform'
      }} data-id="element-10618">
          <canvas ref={canvasRef} className={`block ${!pdfBlob || isLoading ? 'hidden' : ''}`} style={{
          background: 'white'
        }} data-id="element-10619" />
        </div>
      </div>

      {numPages > 1 && !isLoading && !error && <div className={`flex items-center gap-3 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10620">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className={`px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-30 ${darkMode ? 'hover:bg-navy-700 disabled:hover:bg-transparent' : 'hover:bg-gray-200 disabled:hover:bg-transparent'}`} data-id="element-10621">
            ← Prev
          </button>
          <span className="text-xs font-medium" data-id="element-10622">
            Page {currentPage} of {numPages}
          </span>
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= numPages} className={`px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-30 ${darkMode ? 'hover:bg-navy-700 disabled:hover:bg-transparent' : 'hover:bg-gray-200 disabled:hover:bg-transparent'}`} data-id="element-10623">
            Next →
          </button>
        </div>}
    </div>;
}